import type {
  GroupMeetingFormData,
  StoreGroupMeetingDto,
  StoreGroupMeetingTimeframeDto,
} from "meetings/group-meeting/types";
import { toDutchDateString } from "shared/utils/dateHelpers";

/**
 * The group-meeting store endpoint has no opens_at_mode (unlike the
 * student-meeting one): "per direct" resolves to the current school wall-clock
 * moment here, like the transition-meeting form mapper does.
 */
const resolveOpensAt = (
  form: GroupMeetingFormData,
): { date: string | null; time: string } => {
  if (form.opensAtMode === "specific") {
    return { date: form.opensAtDate, time: form.opensAtTime };
  }

  const [date, time] = toDutchDateString(Date.now(), true).split(" ");
  return { date, time };
};

/**
 * The timeframe_settings block shared by the store and update-planning
 * endpoints (both validate it via HasTimeframeSettingsData).
 *
 * Dates stay in DD-MM-YYYY: the server combines `*_date` and `*_time` itself
 * and validates the pair as `d-m-Y H:i`, so no conversion to Y-m-d here —
 * unlike the student-meeting form mapper.
 */
export const groupMeetingTimeframeToDto = (
  form: GroupMeetingFormData,
): StoreGroupMeetingTimeframeDto => {
  const opensAt = resolveOpensAt(form);

  return {
    opens_at_date: opensAt.date,
    opens_at_time: opensAt.time,
    // Not a choice on the form: inviting on open and the 48h closing
    // reminder are default behaviour (the planning step shows them as
    // hints), so a meeting always sends both.
    auto_invite_when_meeting_opens: true,
    closed_no_modify_at_date: form.closedNoModifyDate,
    closed_no_modify_at_time: form.closedNoModifyTime,
    send_reminder_before_closing_no_modify: true,
    archived_at_date: form.archivedDate,
    archived_at_time: form.archivedTime,
    // DateFromTo seeds an empty row on mount; untouched rows must not reach
    // the API (start_at/end_at are required per submitted row).
    meeting_dates: form.meetingDates
      .filter((range) => Boolean(range.start_date))
      .map((range) => ({ ...range })),
  };
};

/** Maps form state to the POST api/school/{school}/group-meetings contract. */
export const groupMeetingFormDataToDto = (
  form: GroupMeetingFormData,
): StoreGroupMeetingDto => {
  return {
    name: form.name,
    message: form.message,
    mentor_classes: [...form.mentorClasses],
    teacher_percentage: form.teacherPercentage ?? 0,
    rating_type_ids: [...form.ratingTypeIds],
    // Classes without a selected teacher are dropped: the server requires
    // min:1 per submitted class (teacher_ids.*), like the legacy model did.
    // Deselected classes may leave a stale teacherIds entry behind (the
    // teachers step only reconciles while mounted) — those never reach the
    // payload.
    teacher_ids: Object.fromEntries(
      Object.entries(form.teacherIds)
        .filter(
          ([mentorClass, teacherIds]) =>
            form.mentorClasses.includes(mentorClass) && teacherIds.length > 0,
        )
        .map(([mentorClass, teacherIds]) => [mentorClass, [...teacherIds]]),
    ),
    timeframe_settings: groupMeetingTimeframeToDto(form),
  };
};
